import { parseDateKey, toDateKey } from './dateKey';

/**
 * Condo stays and household trips, drawn on the calendar as one thin
 * colored bar per stay. A stay is a list of segments: each segment names
 * who is at the condo and carries its own color, so the bar changes
 * color on the day the party changes. Inclusive dates throughout.
 *
 * To add a stay, add a Stay entry. To add a guest mid-stay, end the
 * current segment on the changeover day and start a new segment that
 * same day with the new party and its color. To recolor part of a stay,
 * edit that segment's `color` — colors are data, not CSS.
 *
 * Changeover rule: when two segments of one stay cover the same day
 * (the day one party leaves and the next is alone), the segment that
 * STARTS that day owns it — the departure day takes the new color.
 * The bar itself never breaks at a segment boundary: it connects
 * across the whole stay and rounds only on the stay's real first and
 * last day. Follows the school-break lane rules.
 *
 * The segment's label is printed inside the bar. It starts wherever a
 * visible run of that segment starts within a calendar week row — the
 * segment's first day, a changeover day, or the first cell of a new
 * row the segment continues onto — and is capped at that run's width,
 * truncating with an ellipsis when the run is too narrow for the name.
 */

export interface StaySegment {
  /** Inclusive YYYY-MM-DD. */
  start: string;
  /** Inclusive YYYY-MM-DD. */
  end: string;
  /** Who is there during this segment — first names only, e.g. "Lisa + Kerry". */
  label: string;
  /** CSS color for this segment of the bar. Edit this to recolor it. */
  color: string;
}

export interface Stay {
  /** Slug used as the lane id, e.g. 'aruba-2027'. */
  id: string;
  /** Display name for the whole stay, e.g. 'Aruba trip'. */
  name: string;
  segments: readonly StaySegment[];
}

export const STAYS: readonly Stay[] = [
  {
    id: 'nov-2026-condo',
    name: 'November at the condo',
    segments: [
      // Kerry leaves Nov 10, so her segment ends that day and Lisa's
      // starts it — Nov 10 itself renders in Lisa's color.
      { start: '2026-11-05', end: '2026-11-10', label: 'Lisa + Kerry', color: '#D6336C' },
      { start: '2026-11-10', end: '2026-11-23', label: 'Lisa', color: '#4C9BE8' },
    ],
  },
  {
    id: 'dec-2026-condo',
    name: 'December at the condo',
    segments: [{ start: '2026-12-02', end: '2026-12-08', label: 'Chip + Lisa', color: '#37B24D' }],
  },
  {
    id: 'aruba-2027',
    name: 'Aruba trip',
    segments: [{ start: '2027-01-28', end: '2027-02-07', label: 'Aruba trip', color: '#1AC6D4' }],
  },
];

/** First day of the stay (earliest segment start). */
export function stayStart(stay: Stay): string {
  return stay.segments.reduce((min, s) => (s.start < min ? s.start : min), stay.segments[0].start);
}

/** Last day of the stay (latest segment end). */
export function stayEnd(stay: Stay): string {
  return stay.segments.reduce((max, s) => (s.end > max ? s.end : max), stay.segments[0].end);
}

function segmentCovers(seg: StaySegment, dateKey: string): boolean {
  return dateKey >= seg.start && dateKey <= seg.end;
}

/**
 * The segment of `stay` that owns `dateKey`. When segments share a
 * changeover day, the one starting latest owns the day (see header).
 * Undefined when no segment covers the day.
 */
export function staySegmentOn(stay: Stay, dateKey: string): StaySegment | undefined {
  let best: StaySegment | undefined;
  for (const seg of stay.segments) {
    if (segmentCovers(seg, dateKey) && (!best || seg.start > best.start)) best = seg;
  }
  return best;
}

/** Stays with a segment covering `dateKey`, in list order. */
export function staysOn(dateKey: string): Stay[] {
  return STAYS.filter((s) => staySegmentOn(s, dateKey) !== undefined);
}

export interface StayLane {
  /** Stay id. */
  id: string;
  /** Who is there on this day (the resolved segment's label). */
  label: string;
  /** The resolved segment's color for this day. */
  color: string;
  /** Band continues into the previous cell of this week row. */
  connectLeft: boolean;
  /** Band continues into the next cell of this week row. */
  connectRight: boolean;
  /** Rounded cap. This date is the first day of the stay. */
  roundLeft: boolean;
  /** Rounded cap. This date is the last day of the stay. */
  roundRight: boolean;
  /** A visible run of this segment starts on this cell in this week
   * row, so the in-bar label is printed here. */
  labelStart: boolean;
  /** Days from this cell to the end of this segment's run in this
   * week row (only meaningful when `labelStart` is set). The label is
   * capped at this width so it never spills past the segment. */
  labelRunDays: number;
  /** Readable text color to print the label in on this segment's color. */
  labelColor: string;
}

/**
 * White or dark-ink text, whichever contrasts better against the
 * segment color — white on the deep raspberry, ink on the lighter
 * blue / green / cyan. Keeps future segment colors legible with no
 * extra data.
 */
export function onSegmentColor(hex: string): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return '#FFFFFF';
  const n = parseInt(match[1], 16);
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const lum = 0.2126 * channel(n >> 16) + 0.7152 * channel((n >> 8) & 0xff) + 0.0722 * channel(n & 0xff);
  const whiteContrast = 1.05 / (lum + 0.05);
  const inkContrast = (lum + 0.05) / 0.068; // against #1F2937 (L ≈ 0.018)
  return whiteContrast >= inkContrast ? '#FFFFFF' : '#1F2937';
}

function shiftDateKey(dateKey: string, days: number): string {
  const date = parseDateKey(dateKey);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

/**
 * One lane per stay on that day. The lane connects across the week row
 * when the adjacent cell is the same stay (any segment — a color change
 * mid-stay does not break the bar). Rounded caps are the stay's real
 * first and last day.
 */
export function stayLanes(dateKey: string): StayLane[] {
  const covering = staysOn(dateKey);
  if (covering.length === 0) return [];

  const prevKey = shiftDateKey(dateKey, -1);
  const nextKey = shiftDateKey(dateKey, 1);
  const prevIds = new Set(staysOn(prevKey).map((s) => s.id));
  const nextIds = new Set(staysOn(nextKey).map((s) => s.id));
  const dow = parseDateKey(dateKey).getDay();
  const prevInRow = dow !== 0 && prevKey.slice(0, 7) === dateKey.slice(0, 7);
  const nextInRow = dow !== 6 && nextKey.slice(0, 7) === dateKey.slice(0, 7);

  return covering.map((stay) => {
    const seg = staySegmentOn(stay, dateKey)!;
    const continuesPrev = prevIds.has(stay.id);
    const continuesNext = nextIds.has(stay.id);
    // The in-bar label starts where a run of THIS segment starts in
    // this row: the segment's first day, a changeover day (yesterday
    // belonged to a different segment of the same stay), or the first
    // cell of a row the segment continues onto.
    const prevSeg = prevInRow ? staySegmentOn(stay, prevKey) : undefined;
    const labelStart = prevSeg !== seg;
    let labelRunDays = 0;
    if (labelStart) {
      labelRunDays = 1;
      let cursor = dateKey;
      for (;;) {
        const nk = shiftDateKey(cursor, 1);
        if (parseDateKey(nk).getDay() === 0 || nk.slice(0, 7) !== dateKey.slice(0, 7)) break;
        if (staySegmentOn(stay, nk) !== seg) break;
        labelRunDays += 1;
        cursor = nk;
      }
    }
    return {
      id: stay.id,
      label: seg.label,
      color: seg.color,
      connectLeft: continuesPrev && prevInRow,
      connectRight: continuesNext && nextInRow,
      roundLeft: !continuesPrev,
      roundRight: !continuesNext,
      labelStart,
      labelRunDays,
      labelColor: onSegmentColor(seg.color),
    };
  });
}

/**
 * How many school slots sit under the stay bar.
 * Skidmore owns the bottom slot and Barnstable the slot above it, so a
 * stay takes the first free slot and never paints over either school.
 * Each additional stay covering the same day stacks one slot higher.
 */
export function stayBandRaise(hasBarnstable: boolean, hasSkidmore: boolean): 0 | 1 | 2 {
  if (hasSkidmore && hasBarnstable) return 2;
  if (hasSkidmore) return 1;
  return 0;
}

function formatStayDay(iso: string): string {
  return parseDateKey(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Full inclusive range of the whole stay, e.g. "Jan 28, 2027 – Feb 7, 2027". */
export function stayDateRange(stay: Stay): string {
  return `${formatStayDay(stayStart(stay))} – ${formatStayDay(stayEnd(stay))}`;
}

/** Full inclusive range of one segment, e.g. "Nov 5, 2026 – Nov 10, 2026". */
export function segmentDateRange(seg: StaySegment): string {
  return `${formatStayDay(seg.start)} – ${formatStayDay(seg.end)}`;
}
